import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/store/Auth'
import routes from './routes.js'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

/**
 * Key under which the last visited URL is persisted.
 */
const LAST_URL_STORAGE_KEY = 'anresis-guide:last-url'

/**
 * Utility routes that should never be remembered as a landing destination, as
 * restoring to them on launch would not make sense for the user.
 */
const NON_RESTORABLE_ROUTES = ['loading', 'showcase', 'login']

/**
 * Guards against the very first navigation being handled more than once. The
 * "remember last URL" redirect must only ever fire on the initial load, so that
 * navigating from page to page afterwards keeps working normally.
 */
let initialNavigationHandled = false

/**
 * The path the document was actually loaded at, captured at module evaluation
 * time — before vue-router resolves the "/" -> default-edition redirect, so it
 * still reflects the real entry URL.
 *
 * This matters because switching editions happens through full-page `<a href>`
 * links (see AguideSettingsDrawer), so every edition change is a brand-new
 * document load that re-runs this module and resets `initialNavigationHandled`.
 * Without this signal the restore guard cannot tell a genuine PWA relaunch
 * (which lands on the manifest `start_url`) apart from a user deliberately
 * opening another edition, and would keep bouncing them back to the remembered
 * URL — making it impossible to leave that edition while in standalone mode.
 */
const entryPath =
  typeof window !== 'undefined' ? window.location.pathname : '/'

/**
 * Reduce a route to its first-level path (e.g. "/ksb/foo?x=1" -> "/ksb"). The
 * editions and other landing destinations live at the first level, so query,
 * hash and any deeper segments are intentionally dropped.
 *
 * @param {string} path The route path
 * @returns {string}
 */
const toFirstLevelPath = (path) => `/${path.split('/')[1] ?? ''}`

/**
 * Whether the app is currently running as an installed PWA (standalone display
 * mode). `navigator.standalone` covers iOS Safari, the media query covers the
 * rest.
 *
 * @returns {boolean}
 */
const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true

/**
 * Whether the document entered at the manifest `start_url` ("/"), i.e. this is
 * a genuine (re)launch rather than the user opening a specific edition. Only
 * then should the remembered URL be restored; a concrete entry path is a
 * deliberate destination and must be respected. The router base is stripped so
 * this holds regardless of `BASE_URL`.
 *
 * @returns {boolean}
 */
const launchedAtStartUrl = () => {
  const base = import.meta.env.BASE_URL || '/'
  const withoutBase = entryPath.startsWith(base)
    ? entryPath.slice(base.length)
    : entryPath
  return withoutBase.replace(/^\/+|\/+$/g, '') === ''
}

/**
 * Read the last remembered URL from local storage, tolerating environments
 * where storage access throws (e.g. privacy mode).
 *
 * @returns {string|null}
 */
const readLastUrl = () => {
  try {
    return localStorage.getItem(LAST_URL_STORAGE_KEY)
  } catch {
    return null
  }
}

/**
 * Persist the last visited URL, tolerating environments where storage access
 * throws (e.g. privacy mode).
 *
 * @param {string} path The first-level path to remember
 */
const writeLastUrl = (path) => {
  try {
    localStorage.setItem(LAST_URL_STORAGE_KEY, path)
  } catch {
    // Ignore: remembering the last URL is a convenience, not a requirement.
  }
}

/**
 * Check the permissions of a user and route
 *
 * @param {object} to The route to navigate to
 */
const checkPermissions = (to) => {
  const authStore = useAuthStore()
  if (to.meta.requiresAuth && !authStore.isLoggedIn) {
    return false
  }
  return true
}

router.beforeEach((to, from, next) => {
  const goToLogin = () => {
    return next({ name: 'login' })
  }

  // When an installed PWA is (re)launched it starts at the manifest `start_url`
  // ("/"). In that case only, send the user back to the URL they last used so
  // they don't have to navigate to their edition every time. We must NOT do
  // this when the document entered at a concrete edition path: because editions
  // are switched via full-page links, that is the user deliberately opening
  // another edition, and hijacking it would trap them on the remembered URL.
  if (!initialNavigationHandled) {
    initialNavigationHandled = true

    if (isStandalone() && launchedAtStartUrl()) {
      const lastUrl = readLastUrl()
      if (lastUrl && lastUrl !== toFirstLevelPath(to.path)) {
        return next(lastUrl)
      }
    }
  }

  if (!checkPermissions(to)) {
    return goToLogin()
  }

  document.title = `${to.name} | Anresis guide` ?? 'Anresis guide'

  return next()
})

router.afterEach((to) => {
  // Remember the last meaningful URL so an installed PWA can restore it on the
  // next launch. Utility routes are skipped as they make poor landing pages.
  if (!NON_RESTORABLE_ROUTES.includes(to.name)) {
    writeLastUrl(toFirstLevelPath(to.path))
  }
})

export default router
